import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { FindingItem, TermChangeKind } from "../../../api/content/reviewDecoders";
import { appPaths } from "../../../shared/routing/appPaths";
import { iriLocalName } from "../../../shared/references/shortReferences";
import type { ConflictRow } from "./conflictRows";
import Chip from "../../../ui/feedback/Chip";
import { regressedText } from "./regressedText";
import { itemKindText } from "../../workflows/inputCards/typeWords";

type TermLabels = Readonly<Record<string, string>>;

/** One row of the conflicts list: an item and the changed terms it relies on, or a single finding. */
export default function ConflictRowView({
  row,
  labels,
  changedItemIds,
  onFocusItem,
}: {
  row: ConflictRow;
  labels: TermLabels;
  changedItemIds: ReadonlySet<string>;
  onFocusItem: (itemId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const changes = (kinds: readonly TermChangeKind[]) => kinds.map((kind) => t(`reviews.termChanges.${kind}`)).join("; ");
  const itemName = (item: FindingItem) => <ItemName item={item} inChangeSet={changedItemIds.has(item.itemId)} onFocusItem={onFocusItem} />;

  if (row.kind === "item_relies_on") {
    const renamed = row.terms.filter((term) => term.renamedTo !== null);
    return (
      <li className="app-surface space-y-0.5 rounded border px-3 py-2 text-sm">
        <p>
          <Chip className="mr-1.5">{itemKindText(row.item.kind, t)}</Chip>
          {itemName(row.item)}{" "}
          {t("reviews.conflicts.reliesOn")}{" "}
          {joinedList(
            row.terms.map((term) => <TermName key={term.iri} iri={term.iri} labels={labels} />),
            i18n.resolvedLanguage,
          )}
          .
        </p>
        <p className="app-text-muted text-xs first-letter:uppercase">
          {changes(row.changeKinds)}
          {renamed.map((term) => (
            <span key={term.iri}>
              {". "}
              {t("reviews.conflicts.renamed", { term: iriLocalName(term.iri), renamedTo: iriLocalName(term.renamedTo ?? "") })}
            </span>
          ))}
          {row.via.map((reference) => (
            <span key={reference}>
              {" "}
              <Link to={appPaths.item(reference)} className="app-link underline">
                {t("reviews.conflicts.via")}
              </Link>
            </span>
          ))}
        </p>
      </li>
    );
  }

  const finding = row.finding;
  const term = finding.term !== null ? iriLocalName(finding.term) : "";
  let text: string | null | undefined;
  if (finding.kind === "possible_duplicate") {
    text = finding.message;
  } else if (finding.kind === "derived_source_changed") {
    text = t("reviews.conflicts.derivedSourceChanged");
  } else if (finding.kind === "check_regressed") {
    text = regressedText({ checkId: finding.checkId ?? "", hasItem: finding.item !== null }, t);
  } else if (finding.kind === "unused_term_changed") {
    text = t("reviews.conflicts.unused", { term, changes: changes(finding.termChangeKinds) });
  } else {
    text = `${term}: ${changes(finding.termChangeKinds)}`;
  }
  return (
    <li className="app-surface rounded border px-3 py-2 text-sm" title={finding.kind === "check_regressed" ? (finding.checkId ?? undefined) : undefined}>
      {finding.item !== null ? <span className="mr-1.5">{itemName(finding.item)}</span> : null}
      {text}
      {finding.renamedTo !== null && finding.term !== null ? (
        <span className="app-text-muted">
          {" "}
          {t("reviews.conflicts.renamed", { term: iriLocalName(finding.term), renamedTo: iriLocalName(finding.renamedTo) })}
        </span>
      ) : null}
    </li>
  );
}

function ItemName({ item, inChangeSet, onFocusItem }: { item: FindingItem; inChangeSet: boolean; onFocusItem: (itemId: string) => void }) {
  return inChangeSet ? (
    <button
      type="button"
      className="app-link font-semibold underline"
      onClick={() => {
        onFocusItem(item.itemId);
      }}
    >
      {item.name}
    </button>
  ) : (
    <Link to={appPaths.item(item.reference)} className="app-link font-semibold underline">
      {item.name}
    </Link>
  );
}

/** A term by the end of its IRI; its label, if it says something else, and the IRI on hover. */
function TermName({ iri, labels }: { iri: string; labels: TermLabels }) {
  const name = iriLocalName(iri);
  const label = labels[iri];
  return (
    <span className="font-medium" title={label === undefined || label === name ? iri : `${label}\n${iri}`}>
      {name}
    </span>
  );
}

/** Elements joined as a list in the reader's language: "A, B and C". */
function joinedList(elements: readonly ReactNode[], language: string | undefined): ReactNode {
  const parts = new Intl.ListFormat(language, { type: "conjunction" }).formatToParts(elements.map((_, index) => String(index)));
  return parts.map((part, index) =>
    part.type === "element" ? <Fragment key={index}>{elements[Number(part.value)]}</Fragment> : <Fragment key={index}>{part.value}</Fragment>,
  );
}
